/**
 * If the shell is up and the updater is still idle, run one check.
 *
 * The main process replays a result it already has. This covers the case
 * where that result never arrived (listeners missed it, and nothing is cached),
 * so the sidebar is not stuck until the next hourly check.
 */

import { useEffect, useRef } from 'react';
import { useUpdateContext } from './useUpdateContext';

/** Wait for a replayed startup result before starting a second check. */
export const SHELL_UPDATE_CHECK_DELAY_MS = 1500;

export function useEnsureUpdateCheck(): void {
  const { status, checkForUpdates } = useUpdateContext();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    if (status !== 'idle') {
      started.current = true;
      return;
    }
    const timer = setTimeout(() => {
      if (started.current) return;
      started.current = true;
      checkForUpdates();
    }, SHELL_UPDATE_CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [status, checkForUpdates]);
}
